import React, { useCallback, useEffect, useState } from "react";
import { FiAlertTriangle, FiCheckCircle, FiInfo, FiZap, FiGlobe } from "react-icons/fi";
import { Modal } from "./Modal";
import { LoadingSpinner } from "./LoadingSpinner";
import { client } from "../Clients";

type Issue = {
  field: string;
  severity: string;
  message: string;
  suggestion?: string | null;
  original?: string | null;
};

type FieldResult = {
  field: string;
  fieldTitle: string;
  score: number;
  issues: Issue[];
};

type AnalysisResult = {
  success: boolean;
  language: string;
  model: string;
  overallScore: number;
  fieldResults: FieldResult[];
  issues: Issue[];
  summary?: string | null;
  inputTokens: number;
  outputTokens: number;
  analyzedAt: string;
  errorMessage?: string | null;
  dataHash?: string | null;
};

type CachedResults = {
  mistakes?: AnalysisResult | null;
  seo?: AnalysisResult | null;
};

interface AIAnalyzeModalProps {
  show: boolean;
  onClose: () => void;
  schemaKey: string;
  language: string;
  contentId?: string;
  slug?: string;
  data: Record<string, any>;
  analyzableFields?: string[];
  /** Cached analysis JSON from the content document (persisted across reloads). */
  cachedResult?: string | null;
  cachedHash?: string | null;
  /** Save the analysis back to the content document for persistence. */
  onResultCached?: (resultJson: string, hash: string) => void;
}

const scoreColor = (score: number) => {
  if (score >= 90) return { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", bar: "bg-emerald-500" };
  if (score >= 70) return { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", bar: "bg-amber-500" };
  return { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", bar: "bg-rose-500" };
};

const severityIcon = (severity: string) => {
  switch (severity) {
    case "error": return <FiAlertTriangle className="h-3.5 w-3.5 text-rose-500" />;
    case "warning": return <FiAlertTriangle className="h-3.5 w-3.5 text-amber-500" />;
    default: return <FiInfo className="h-3.5 w-3.5 text-blue-500" />;
  }
};

/** AI content analysis modal (grammar, spelling, SEO). */
const AIAnalyzeModal: React.FC<AIAnalyzeModalProps> = ({
  show, onClose, schemaKey, language, contentId, slug, data,
  analyzableFields, cachedResult, cachedHash, onResultCached
}) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState<CachedResults>({});
  const [activeTab, setActiveTab] = useState<"mistakes" | "seo">("mistakes");
  const [loaded, setLoaded] = useState(false);

  // Load cached results on open (both tabs from one JSON blob)
  useEffect(() => {
    if (show && cachedResult && !loaded) {
      try {
        const parsed = JSON.parse(cachedResult) as CachedResults;
        // Validate hash — if data changed, results are stale
        const mistakesValid = parsed.mistakes?.dataHash && parsed.mistakes.dataHash === cachedHash;
        const seoValid = parsed.seo?.dataHash && parsed.seo.dataHash === cachedHash;
        setResults({
          mistakes: mistakesValid ? parsed.mistakes : null,
          seo: seoValid ? parsed.seo : null
        });
      } catch { /* ignore */ }
      setLoaded(true);
    }
    if (show && !cachedResult && !loaded) {
      setLoaded(true);
    }
  }, [show, cachedResult, cachedHash, loaded]);

  // Reset loaded state when modal closes
  useEffect(() => {
    if (!show) setLoaded(false);
  }, [show]);

  const currentResult = results[activeTab] ?? null;

  const persistResults = useCallback((updated: CachedResults) => {
    if (!onResultCached) return;
    const hash = updated.mistakes?.dataHash ?? updated.seo?.dataHash ?? "";
    if (hash) {
      onResultCached(JSON.stringify(updated), hash);
    }
  }, [onResultCached]);

  const handleAnalyze = useCallback(async () => {
    setAnalyzing(true);
    try {
      const res = await client.ai.apiAiAnalyzePost({
        schemaKey,
        language,
        contentId: contentId ?? undefined,
        slug: slug ?? undefined,
        data: {}, // Empty — backend fetches fresh from database for reliable JSON structure
        analyzableFields: analyzableFields ?? undefined,
        analysisType: activeTab
      } as any);
      const r = res.data as any as AnalysisResult;
      const updated = { ...results, [activeTab]: r };
      setResults(updated);
      persistResults(updated);
    } catch (err: any) {
      const errorResult: AnalysisResult = {
        success: false, language, model: "o3", overallScore: 0, fieldResults: [], issues: [],
        inputTokens: 0, outputTokens: 0, analyzedAt: new Date().toISOString(),
        errorMessage: err?.response?.data?.errorMessage ?? err?.message ?? "Analysis failed."
      };
      setResults(prev => ({ ...prev, [activeTab]: errorResult }));
    } finally {
      setAnalyzing(false);
    }
  }, [schemaKey, language, contentId, slug, data, analyzableFields, activeTab, results, persistResults]);

  return (
    <Modal show={show} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <div className="flex items-center gap-2">
            <FiZap className="h-5 w-5 text-purple-600" />
            AI Content Analysis
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          {/* Tab selector — switching preserves results */}
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setActiveTab("mistakes")}
              className={`relative rounded-full px-4 py-1.5 text-xs font-semibold transition ${activeTab === "mistakes" ? "bg-purple-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              <FiZap className="mr-1.5 inline h-3 w-3" /> Grammar & Spelling
              {results.mistakes?.success && (
                <span className={`ml-2 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${results.mistakes.overallScore >= 90 ? "bg-emerald-200 text-emerald-800" : results.mistakes.overallScore >= 70 ? "bg-amber-200 text-amber-800" : "bg-rose-200 text-rose-800"}`}>
                  {results.mistakes.overallScore}
                </span>
              )}
            </button>
            <button type="button" onClick={() => setActiveTab("seo")}
              className={`relative rounded-full px-4 py-1.5 text-xs font-semibold transition ${activeTab === "seo" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              <FiGlobe className="mr-1.5 inline h-3 w-3" /> SEO Analysis
              {results.seo?.success && (
                <span className={`ml-2 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${results.seo.overallScore >= 90 ? "bg-emerald-200 text-emerald-800" : results.seo.overallScore >= 70 ? "bg-amber-200 text-amber-800" : "bg-rose-200 text-rose-800"}`}>
                  {results.seo.overallScore}
                </span>
              )}
            </button>
          </div>

          {!currentResult && !analyzing && (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center">
              <FiZap className="mx-auto h-8 w-8 text-purple-400" />
              <p className="mt-3 text-sm font-semibold text-slate-700">
                {activeTab === "seo" ? "SEO quality analysis" : "Grammar & spelling check"}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {analyzableFields && analyzableFields.length > 0
                  ? `Analyzing ${analyzableFields.length} configured field${analyzableFields.length !== 1 ? "s" : ""} with OpenAI o3.`
                  : "Analyzing all text fields with OpenAI o3."}
              </p>
              <button type="button" onClick={handleAnalyze}
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-purple-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-purple-700">
                <FiZap className="h-4 w-4" /> Start Analysis
              </button>
            </div>
          )}

          {analyzing && (
            <div className="flex flex-col items-center gap-4 py-10">
              <LoadingSpinner size="lg" />
              <p className="text-sm font-medium text-slate-600">Analyzing content with AI...</p>
              <p className="text-xs text-slate-400">This may take 10-30 seconds.</p>
            </div>
          )}

          {currentResult && (
            <>
              {currentResult.errorMessage && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{currentResult.errorMessage}</div>
              )}

              {currentResult.success && (
                <>
                  {/* Overall score */}
                  <div className={`rounded-2xl border p-5 ${scoreColor(currentResult.overallScore).bg} ${scoreColor(currentResult.overallScore).border}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Overall Score</p>
                        <p className={`text-3xl font-bold ${scoreColor(currentResult.overallScore).text}`}>{currentResult.overallScore}/100</p>
                        <p className="mt-1 text-sm text-slate-600">{currentResult.summary}</p>
                      </div>
                      <div className="h-16 w-16">
                        <svg viewBox="0 0 36 36" className="h-16 w-16 -rotate-90">
                          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                          <circle cx="18" cy="18" r="15.9" fill="none"
                            stroke={currentResult.overallScore >= 90 ? "#10b981" : currentResult.overallScore >= 70 ? "#f59e0b" : "#ef4444"}
                            strokeWidth="3" strokeDasharray={`${currentResult.overallScore} ${100 - currentResult.overallScore}`} strokeLinecap="round" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Per-field results */}
                  {currentResult.fieldResults.length > 0 && (
                    <div className="space-y-3">
                      {currentResult.fieldResults.map((fr, idx) => {
                        const sc = scoreColor(fr.score);
                        return (
                          <div key={idx} className={`rounded-xl border ${sc.border} overflow-hidden`}>
                            <div className={`flex items-center justify-between px-4 py-3 ${sc.bg}`}>
                              <div className="flex items-center gap-3">
                                {fr.score >= 90 ? <FiCheckCircle className={`h-4 w-4 ${sc.text}`} /> : <FiAlertTriangle className={`h-4 w-4 ${sc.text}`} />}
                                <div>
                                  <p className="text-sm font-semibold text-slate-900">{fr.fieldTitle || fr.field}</p>
                                  <p className="text-xs text-slate-500">{fr.issues.length} issue{fr.issues.length !== 1 ? "s" : ""}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="h-2 w-20 rounded-full bg-slate-200">
                                  <div className={`h-2 rounded-full ${sc.bar}`} style={{ width: `${fr.score}%` }} />
                                </div>
                                <span className={`text-sm font-bold ${sc.text}`}>{fr.score}</span>
                              </div>
                            </div>
                            {fr.issues.length > 0 && (
                              <div className="divide-y divide-slate-100 bg-white">
                                {fr.issues.map((issue, i) => (
                                  <div key={i} className="px-4 py-3">
                                    <div className="flex items-start gap-2">
                                      {severityIcon(issue.severity)}
                                      <div className="min-w-0 flex-1">
                                        <p className="text-sm text-slate-800">{issue.message}</p>
                                        {issue.original && (
                                          <p className="mt-1 text-xs text-slate-500">
                                            <span className="line-through decoration-rose-400">{issue.original}</span>
                                          </p>
                                        )}
                                        {issue.suggestion && (
                                          <p className="mt-0.5 text-xs font-medium text-emerald-700">{issue.suggestion}</p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex items-center justify-between text-[11px] text-slate-400">
                    <span>Model: {currentResult.model} · {currentResult.inputTokens + currentResult.outputTokens} tokens · Analyzed {new Date(currentResult.analyzedAt).toLocaleString()}</span>
                    <button type="button" onClick={handleAnalyze}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50">
                      <FiZap className="h-3 w-3" /> Re-analyze
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" onClick={onClose}
          className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AIAnalyzeModal;
